import axios from "axios";
import Cookies from "js-cookie";
import {store} from "../../";
import { ADD_NOTIFICATION } from "../../store/types";
import * as Sentry from "@sentry/react";
import { getAccountId, getFacilityId } from "../../utilis/facilityStorage";
import { sanitizeError } from "../../utilis/sentry-sanitization";
import { refreshManager } from "../auth/refreshManager";

const baseURL = process.env.REACT_APP_BASE_URL;

if (process.env.NODE_ENV !== "development" && (!baseURL || baseURL.includes("localhost"))) {
  const env = process.env.NODE_ENV;
  Sentry.captureMessage(`⚠️ Invalid Axios baseURL configuration in ${env}`, "warning");
}

const api = axios.create({ baseURL, withCredentials: true });

// Normalize backend error shapes so frontend can rely on consistent fields
const getErrorMeta = (error) => {
  const response = error?.response;
  const data = response?.data || {};
  const nestedError = data.error || {};

  const statusCode = data.statusCode || response?.status || null;
  const errorCode = data.errorCode || data.code || nestedError.code || null;
  const message =
    nestedError.message ||
    data.message ||
    data.error ||
    (statusCode === 400
      ? "Bad request"
      : statusCode === 401
      ? "Unauthorized"
      : statusCode === 403
      ? "Forbidden"
      : statusCode === 404
      ? "Not found"
      : statusCode === 429
      ? "Too many requests. Please slow down."
      : "Internal server error");

  return { statusCode, errorCode, message, data };
};

const redirectToLogin = (message = null) => {
  if (message) {
    store.dispatch({
      type: ADD_NOTIFICATION,
      payload: {
        type: "error",
        label: message,
        id: "sessionError",
      },
    });
  }
  sessionStorage.clear();
  window.location.href = '/login';
};

api.interceptors.request.use(
  async config => {
    const idToken = sessionStorage.getItem("x-auth");
    // Use utility functions (NOT hooks) - these read from Redux store directly
    // Hooks can't be used in interceptors, but utility functions can
    let accountId = getAccountId(); // Reads from Redux first (tab-specific), then localStorage
    let facilityId = getFacilityId(); // Reads from Redux first (tab-specific), then localStorage

    if (idToken) {
      config.headers["x-auth"] = idToken;
      config.headers["accountId"] = accountId;
      config.headers["facilityId"] = facilityId;
      config.withCredentials = true;
      config.credentials = true;
    }

    const safeMethods = ['GET', 'HEAD', 'OPTIONS'];
    const method = config.method?.toUpperCase();

    if (!safeMethods.includes(method)) {
      // Get CSRF token from cookie
      const csrfToken = Cookies.get("__Secure-CSRF-Token") || Cookies.get("CSRF-Token");

      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken;
      } else if (process.env.NODE_ENV === 'development') {
        console.warn('[CSRF] Token missing for request:', config.method, config.url);
      }
    }

    return config;
  },
  error => {
    const sanitizedError = sanitizeError(error);
    Sentry.captureException(sanitizedError);
    Sentry.captureMessage('Axios request error', 'error');
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  response => response.data,
  async error => {
    const statusCode = error.response?.status;
    const requestURL = error.config?.url;
    const originalRequest = error.config;
    const { errorCode, message } = getErrorMeta(error);

    const sanitizedError = sanitizeError(error);
    const urlPath = requestURL ? new URL(requestURL, 'http://dummy').pathname : 'unknown';

    if (statusCode === 401) {
      if (errorCode === 'SESSION_COMPROMISED') {
        Sentry.captureMessage('Session compromised - token reuse detected', 'warning');
        redirectToLogin('Session security issue detected. Please log in again.');
        return Promise.reject(error);
      }

      if (originalRequest._retry || originalRequest.url?.includes('/refresh')) {
        redirectToLogin('Your session has expired. Please log in again.');
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      try {
        const newToken = await refreshManager.refresh();
        originalRequest.headers['x-auth'] = newToken;

        // Re-read CSRF token — refresh may have set a new one
        const freshCsrfToken = Cookies.get("__Secure-CSRF-Token") || Cookies.get("CSRF-Token");
        if (freshCsrfToken) {
          originalRequest.headers['X-CSRF-Token'] = freshCsrfToken;
        }

        return api(originalRequest);
      } catch (refreshError) {
        const refreshErrorCode = refreshError.response?.data?.code;
        if (refreshErrorCode === 'SESSION_COMPROMISED') {
          Sentry.captureMessage('Session compromised during refresh', 'warning');
          redirectToLogin('Session security issue detected. Please log in again.');
        } else {
          redirectToLogin('Your session has expired. Please log in again.');
        }
        return Promise.reject(refreshError);
      }
    } else if (statusCode === 403) {
      Sentry.captureException(sanitizedError);
      Sentry.captureMessage(`Axios response error ${statusCode} on ${urlPath}`, 'error');

      if (errorCode === 'CSRF_TOKEN_MISSING' ||
          errorCode === 'CSRF_TOKEN_MISMATCH' ||
          errorCode === 'INVALID_ORIGIN') {

        // One silent retry with fresh CSRF token before logging out (skip for origin errors)
        if (!originalRequest._csrfRetry && errorCode !== 'INVALID_ORIGIN') {
          originalRequest._csrfRetry = true;
          const freshCsrfToken = Cookies.get("__Secure-CSRF-Token") || Cookies.get("CSRF-Token");
          if (freshCsrfToken) {
            originalRequest.headers['X-CSRF-Token'] = freshCsrfToken;
            return api(originalRequest);
          }
        }

        store.dispatch({
          type: ADD_NOTIFICATION,
          payload: {
            type: "error",
            label: "Your session has expired or is invalid. Please log in again.",
            id: "csrfError",
          },
        });

        Sentry.captureMessage(`403 CSRF validation failed: ${errorCode}`, "warning");

        setTimeout(() => {
          sessionStorage.clear();
          window.location.href = '/login';
        }, 2000);
      } else {
        store.dispatch({
          type: ADD_NOTIFICATION,
          payload: {
            type: "error",
            label: "Access denied: You don't have permission to perform this action.",
            id: "forbiddenError",
          },
        });

        Sentry.captureMessage("403 Forbidden - Access denied", "warning");
      }
    } else if (statusCode === 429) {
      Sentry.captureException(sanitizedError);
      Sentry.captureMessage(`Axios response error ${statusCode} on ${urlPath}`, 'error');
      
      const retryAfter = error.response.headers["retry-after"];
      const msg = message || "Too many requests. Please slow down.";

      store.dispatch({
        type: ADD_NOTIFICATION,
        payload: {
          type: "error",
          label: retryAfter
            ? `${msg} Try again in ${retryAfter} seconds.`
            : `${msg}`,
          id: "rateLimitError",
        },
      });

      Sentry.captureMessage(`429 Rate limit triggered`, 'warning');
    } else {
      Sentry.captureException(sanitizedError);
      Sentry.captureMessage(`Axios response error ${statusCode} on ${urlPath}`, 'error');
    }
    return Promise.reject(error);
  }
);

export default api;
